<template>
    <div class="column is-paddingless"
        v-if="space.id && !detail.loading && note_id && detail.data.id">

        <div class="columns is-variable is-0 content-wrapper">
            <div class="column note-details scroll" ref="content">
                <div class="editor-container">
                    <!-- Tags and full screen button -->
                    <note-details-top-panel
                        @toggle_sidebars="$emit('toggle_sidebars', arguments[0])"
                        :show_sidebars="show_sidebars"
                        :note="detail.data"
                        :space="space"/>

                    <input
                        v-model="form.name"
                        @blur="save(note_id)"
                        @input="autosave(note_id)"
                        :disabled="!editable"
                        class="title note-title is-3 has-text-weight-bold has-text-grey-dark"
                        placeholder="Untitled Note"
                        name="title"
                        ref="title"
                        type="text"/>

                    <base-backend-errors :data="$store.state.attachments.create.error"/>

                    <editor
                        v-if="loaded"
                        v-model="form.content"
                        :space="space"
                        :note="detail.data"
                        :disabled="!editable"
                        @input="autosave(note_id)"
                        @blur="save(detail.data.id)"
                        ref="editor"/>
                </div>
            </div>

            <note-details-attachment-list
                v-if="show_attachments"
                class="hide-on-print right-sidebar column is-narrow scroll"
                :space="space"
                :note="detail.data"
                :attachments="detail.data.attachments"
                :is_editable="editable"
                @toggle_attachments="toggle_attachments"/>

            <comments-list
                v-if="show_comments"
                :space="space"
                :note="detail.data"
                class="hide-on-print right-sidebar column is-narrow"
                @toggle_comments="toggle_comments"/>

            <note-details-contents-list
                v-if="show_contents_list"
                class="hide-on-print right-sidebar column is-narrow scroll"
                :headings="headings"
                :content_ref="$refs.content"
                @toggle_content_list="toggle_content_list"/>

            <note-details-versions-list
                v-if="show_revision"
                class="hide-on-print right-sidebar column is-narrow scroll"
                :space="space"
                :note="detail.data"
                @toggle_revision="toggle_revision"
                @restored="load_details"/>

            <card-details
                v-if="show_card_details"
                :column="card_details.column"
                :in_note_editor="true"
                :card="card_details"
                :space="space"
                @close="toggle_card_details"
                class="right-sidebar"/>

            <note-details-actions
                :space="space"
                :note="detail.data"
                :show_sidebars="show_sidebars"
                @toggle_contents_list="toggle_content_list"
                @toggle_card_details="toggle_card_details"
                @toggle_attachments="toggle_attachments"
                @toggle_revision="toggle_revision"
                @toggle_comments="toggle_comments"
                class="column is-narrow"/>

        </div>
    </div>

    <note-not-selected v-else-if="!note_id || !detail.data.id" class="column is-paddingless"/>

    <!-- TODO: change to skeleton loading view -->
    <div v-else class="column is-paddingless">
        <div class="editor-container">Loading...</div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import NoteDetailsAttachmentList from './NoteDetailsAttachmentList.vue';
import NoteDetailsContentsList from './NoteDetailsContentsList.vue';
import NoteDetailsVersionsList from './NoteDetailsVersionsList.vue';
import NoteDetailsTopPanel from './NoteDetailsTopPanel.vue';
import NoteDetailsActions from './NoteDetailsActions.vue';
import CommentsList from '../comment/CommentsList.vue';
import NoteNotSelected from './NoteNotSelected.vue';
import CardDetails from '../flows/CardDetails.vue';
import { clean_charts } from '../../utils/charts';
import Editor from './Editor.vue';

export default {
    name: 'NoteDetails',
    props: ['space', 'show_sidebars'],
    components: {
        CardDetails,
        Editor,
        CommentsList,
        NoteDetailsAttachmentList,
        NoteDetailsVersionsList,
        NoteDetailsContentsList,
        NoteDetailsTopPanel,
        NoteDetailsActions,
        NoteNotSelected,
    },
    data() {
        return {
            form: { name: null, content: null },
            show_contents_list: false,
            show_attachments: false,
            show_card_details: false,
            show_revision: false,
            show_comments: false,
            card_details: null,
            loaded: false,
            headings: [],
        };
    },
    computed: {
        ...mapState('notes', ['list', 'detail', 'update']),
        ...mapState('attachments', { image_upload: 'create' }),
        note_id() {
            return parseInt(this.$route.query.note, 10);
        },
        editable() {
            const note = this.detail.data;
            return this.space_topic_permission('NOTE_UPDATE', note.topic) && !note.is_locked;
        },
    },
    watch: {
        space: {
            immediate: true,
            handler() {
                this.load_details();
            },
        },
        $route(new_router, old_route) {
            if (new_router.query.note !== old_route.query.note) {
                this.load_details();
            }
        },
        show_sidebars(value) {
            if (value) {
                this.show_contents_list = false;
                this.show_card_details = false;
                this.show_attachments = false;
                this.show_revision = false;
                this.show_comments = false;
            }
        },
    },
    methods: {
        async load_details() {
            this.loaded = false;
            if (!this.space.id || !this.note_id) return;

            const details = await this.$store.dispatch('notes/detail', {
                space_id: this.space.id,
                note_id: this.note_id,
            });

            this.form.name = details.name;
            this.form.content = details.content || '';
            this.loaded = true;

            if (this.$route.query.created) {
                this.$nextTick(() => this.$refs.title.focus());
            }

            this.$store.commit('notes/set_as_read', this.note_id);
        },
        autosave: debounce(function (note_id) {
            // If user didn't select another note before we save previous
            if (note_id === this.note_id) {
                this.save(note_id);
            }

            // Update headings (Table of content) list
            if (this.show_contents_list) {
                this.headings = this.get_headings();
            }
        }, 1000),
        save(note_id) {
            const { topic } = this.detail.data;
            if (!note_id) return;
            if (!this.space_topic_permission('NOTE_UPDATE', topic)) return;

            // clean charts (vega) svg before save
            const clean_content = clean_charts(this.form.content);

            // Save only if content or name was changed
            const is_same_name = this.form.name === this.detail.data.name;
            const is_same_content = clean_content === this.detail.data.content;
            if (is_same_name && is_same_content) return;

            this.$store.dispatch('notes/update', {
                space_id: this.space.id,
                update_detail: true,
                note_id,
                data: {
                    name: this.form.name,
                    content: clean_content,
                },
            }).then(() => {
                if ('topic' in this.$route.query || 'private' in this.$route.query) {
                    this.$store.commit('tree/update_note_name', { note_id, name: this.form.name });
                } else {
                    this.$store.commit('notes/update_note_name', { note_id, name: this.form.name });
                }
            });
        },
        toggle_card_details(card) {
            this.card_details = card;
            this.$emit('toggle_sidebars', this.show_card_details);
            this.show_card_details = !this.show_card_details;
            if (this.show_card_details) {
                this.show_comments = false;
                this.show_revision = false;
                this.show_attachments = false;
                this.show_contents_list = false;
            }
        },
        toggle_attachments() {
            this.$emit('toggle_sidebars', this.show_attachments);
            this.show_attachments = !this.show_attachments;
            if (this.show_attachments) {
                this.show_contents_list = false;
                this.show_card_details = false;
                this.show_revision = false;
                this.show_comments = false;
            }
        },
        toggle_content_list() {
            this.$emit('toggle_sidebars', this.show_contents_list);
            this.headings = this.get_headings();
            this.show_contents_list = !this.show_contents_list;
            if (this.show_contents_list) {
                this.show_card_details = false;
                this.show_attachments = false;
                this.show_comments = false;
                this.show_revision = false;
            }
        },
        toggle_revision() {
            this.$emit('toggle_sidebars', this.show_revision);
            this.show_revision = !this.show_revision;
            if (this.show_revision) {
                this.show_comments = false;
                this.show_attachments = false;
                this.show_card_details = false;
                this.show_contents_list = false;
            }
        },
        toggle_comments() {
            this.$emit('toggle_sidebars', this.show_comments);
            this.show_comments = !this.show_comments;
            if (this.show_comments) {
                this.show_revision = false;
                this.show_attachments = false;
                this.show_card_details = false;
                this.show_contents_list = false;
            }
        },
        get_headings() {
            const selector = '.fr-wrapper h1, .fr-wrapper h2, .fr-wrapper h3';
            return this.$refs.editor.$el.querySelectorAll(selector);
        },
    },
};
</script>

<style scoped>
    .content-wrapper {
        margin-top: 0;
    }

    .note-details {
        height: 100vh;
        padding-top: 0;
    }

    .note-title {
        width: 100%;
        padding-left: 0.2rem;
        padding-bottom: 0.4rem;
        line-height: 1.5rem;
        outline: 0;
        border-width: 0;
        background-color: white;
    }

    @media print {
        .note-details { margin-right: 0; }
    }
</style>
